import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2, Package } from "lucide-react";
import {
  Card,
  CardContent,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  BizcoverProductListCard,
  mapProductsToListCard,
} from "@b2b/ui-components";
import { sellerService } from "@b2b/services";

type SellerProduct = Record<string, unknown>;

interface ProductsSectionProps {
  sellerId?: string;
  featuredProductIds?: string[];
  /** Fallback when sellerId is not provided (legacy usage). */
  products?: SellerProduct[];
  featuredProducts?: SellerProduct[];
  pageSize?: number;
  supplierName?: string;
}

const DEFAULT_PAGE_SIZE = 10;

const PRODUCT_GRID_CLASS =
  "grid items-start gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4";

const ProductSkeletonGrid = () => (
  <div className={PRODUCT_GRID_CLASS}>
    {Array.from({ length: 8 }).map((_, index) => (
      <div
        key={`skeleton-${index}`}
        className="animate-pulse overflow-hidden rounded-xl border border-gray-100 bg-white p-2.5"
      >
        <div className="aspect-[4/3] rounded-lg bg-gray-200" />
        <div className="mt-3 space-y-2">
          <div className="h-4 rounded bg-gray-200" />
          <div className="h-4 w-2/3 rounded bg-gray-200" />
          <div className="h-5 w-1/2 rounded bg-gray-200" />
          <div className="h-7 rounded-full bg-gray-200" />
        </div>
      </div>
    ))}
  </div>
);

const ProductGrid = ({
  products,
  title,
  loading,
  supplierName,
}: {
  products: SellerProduct[];
  title?: string;
  loading?: boolean;
  supplierName?: string;
}) => {
  const cardItems = useMemo(
    () => mapProductsToListCard(products, { supplierName }),
    [products, supplierName],
  );

  if (loading) {
    return <ProductSkeletonGrid />;
  }

  if (!products.length) {
    return (
      <div className="py-8 text-center sm:py-10 md:py-12">
        <Package className="mx-auto mb-3 h-12 w-12 text-gray-300 sm:mb-4 sm:h-16 sm:w-16" />
        <h3 className="mb-2 text-base font-semibold text-gray-700 sm:text-lg">
          No Products Available
        </h3>
        <p className="text-sm text-gray-500 sm:text-base">
          This seller has not added any products yet.
        </p>
      </div>
    );
  }

  return (
    <>
      {title ? (
        <div className="mb-4 sm:mb-5 md:mb-6">
          <h3 className="mb-1 text-xl font-bold text-gray-900 sm:mb-2 sm:text-2xl">
            {title}
          </h3>
        </div>
      ) : null}

      <div className={PRODUCT_GRID_CLASS}>
        {cardItems.map((product) => (
          <BizcoverProductListCard key={product.id} {...product} />
        ))}
      </div>
    </>
  );
};

export const ProductsSection = ({
  sellerId,
  featuredProductIds = [],
  products: legacyProducts = [],
  featuredProducts: legacyFeaturedProducts = [],
  pageSize = DEFAULT_PAGE_SIZE,
  supplierName,
}: ProductsSectionProps) => {
  const [page, setPage] = useState(1);
  const [products, setProducts] = useState<SellerProduct[]>(legacyProducts);
  const [featuredProducts, setFeaturedProducts] = useState<SellerProduct[]>(
    legacyFeaturedProducts,
  );
  const [loading, setLoading] = useState(false);
  const [featuredLoading, setFeaturedLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Keep a ref so fetchFeatured always sees the latest IDs without being
  // re-created every time the parent passes a new array reference.
  const featuredProductIdsRef = useRef<string[]>(featuredProductIds);
  featuredProductIdsRef.current = featuredProductIds;

  // Stable serialised key — only changes when the actual IDs change, not on
  // every parent render that produces a new array reference for the same IDs.
  const featuredIdsKey = useMemo(
    () => [...featuredProductIds].sort().join(","),
    [featuredProductIds],
  );
  const [pagination, setPagination] = useState({
    page: 1,
    limit: pageSize,
    total: 0,
    totalPages: 1,
  });

  const fetchPage = useCallback(
    async (pageNum: number) => {
      if (!sellerId) return;
      setLoading(true);
      setError(null);
      try {
        const data = await sellerService.getPublicStorefrontProducts(sellerId, {
          page: pageNum,
          limit: pageSize,
        });
        setProducts((data?.products as SellerProduct[]) || []);
        if (data?.pagination) {
          setPagination(data.pagination);
        }
      } catch (e: unknown) {
        const message =
          e instanceof Error ? e.message : "Failed to load products";
        if (legacyProducts.length > 0) {
          setProducts(legacyProducts);
          setPagination({
            page: pageNum,
            limit: pageSize,
            total: legacyProducts.length,
            totalPages: 1,
          });
          setError(null);
        } else {
          setError(message);
          setProducts([]);
        }
      } finally {
        setLoading(false);
      }
    },
    [sellerId, pageSize, legacyProducts],
  );

  const fetchFeatured = useCallback(async () => {
    const ids = featuredProductIdsRef.current;
    if (!sellerId || ids.length === 0) {
      setFeaturedProducts([]);
      return;
    }
    setFeaturedLoading(true);
    try {
      const data = await sellerService.getPublicStorefrontProducts(sellerId, {
        page: 1,
        limit: 50,
      });
      const all = (data?.products as SellerProduct[]) || [];
      const idSet = new Set(ids);
      setFeaturedProducts(all.filter((p) => idSet.has(String(p.id))));
    } catch {
      setFeaturedProducts([]);
    } finally {
      setFeaturedLoading(false);
    }
  // featuredIdsKey gates re-fetch; ids are read from the ref (always current).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerId, featuredIdsKey]);

  useEffect(() => {
    if (!sellerId) return;
    setPage(1);
  }, [sellerId]);

  useEffect(() => {
    if (!sellerId) return;
    fetchPage(page);
  }, [sellerId, page, fetchPage]);

  useEffect(() => {
    if (!sellerId) return;
    fetchFeatured();
  }, [sellerId, fetchFeatured]);

  useEffect(() => {
    if (sellerId) return;
    setProducts(legacyProducts);
    setFeaturedProducts(legacyFeaturedProducts);
  }, [sellerId, legacyProducts, legacyFeaturedProducts]);

  const totalPages = pagination.totalPages || 1;
  const useServerPagination = Boolean(sellerId);

  return (
    <Card className="border border-gray-100 shadow-sm">
      <CardContent className="p-4 sm:p-5 md:p-6">
        {error ? (
          <p className="mb-4 text-sm text-red-600">{error}</p>
        ) : null}

        <Tabs defaultValue="all" className="w-full">
          <div className="-mx-2 mb-4 overflow-x-auto scrollbar-hide sm:mx-0 sm:mb-6">
            <TabsList className="inline-grid h-auto min-w-[320px] w-full grid-cols-2 rounded-lg border border-gray-200 bg-white p-1 sm:w-auto">
              <TabsTrigger
                value="all"
                className="min-h-[40px] rounded-md text-xs data-[state=active]:bg-[#FF6A00] data-[state=active]:text-white sm:text-sm"
              >
                All Products
                {useServerPagination && pagination.total > 0
                  ? ` (${pagination.total})`
                  : ""}
              </TabsTrigger>
              <TabsTrigger
                value="featured"
                className="min-h-[40px] rounded-md text-xs data-[state=active]:bg-[#FF6A00] data-[state=active]:text-white sm:text-sm"
              >
                Featured Products
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="featured" className="mt-4 sm:mt-6">
            {featuredLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-[#FF6A00]" />
              </div>
            ) : featuredProducts.length === 0 ? (
              <p className="text-sm text-gray-600 sm:text-base">
                No featured products available.
              </p>
            ) : (
              <ProductGrid products={featuredProducts} supplierName={supplierName} />
            )}
          </TabsContent>

          <TabsContent value="all" className="mt-4 sm:mt-6">
            <ProductGrid
              products={products}
              loading={useServerPagination && loading}
              supplierName={supplierName}
            />

            {useServerPagination && totalPages > 1 && !loading ? (
              <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-4">
                <p className="text-xs text-gray-600">
                  Page{" "}
                  <span className="font-semibold tabular-nums">{page}</span> of{" "}
                  <span className="font-semibold tabular-nums">{totalPages}</span>
                  {" · "}
                  <span className="tabular-nums">{pagination.total}</span> products
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1 || loading}
                    className="inline-flex h-8 items-center gap-1 rounded-full border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-700 transition-all hover:border-[#FF6A00] hover:text-[#FF6A00] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                    Prev
                  </button>
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages || loading}
                    className="inline-flex h-8 items-center gap-1 rounded-full border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-700 transition-all hover:border-[#FF6A00] hover:text-[#FF6A00] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Next
                    <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ) : null}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};
